import type { LucideIcon } from "lucide-react";
import {
  Rocket, FolderPlus, Smartphone, PlayCircle, MessageCircle, ArrowRight,
  Check, Users, Settings, FileText, Bell, HelpCircle, Clock, Mail, Play, Folder, Plus,
} from "lucide-react";
import { LocaleLink } from "../i18n/LocaleLink";
import { useLocale } from "../i18n/useLocale";
import { cmsImage } from "../i18n/cmsImage";
import { Container, Eyebrow } from "../components/ui";
import {
  MotionProvider, Reveal, Workflow, type WorkflowStep,
  ConnectedSteps, IconGridCta, type CtaGridIcon, Button as DSButton,
  PageHeading, SectionHeading,
} from "../ds/core";
import { TRIAL_PATH as TRIAL } from "../config/entrypoints";

const U = (id: string) => `https://images.unsplash.com/${id}?w=1800&auto=format&fit=crop&q=80`;
const VIDEO_POSTER = U("photo-1521737604893-d14cc237f11d");

const SCRIM = "linear-gradient(180deg, rgba(8,11,22,0.35) 0%, rgba(8,11,22,0.25) 45%, rgba(8,11,22,0.7) 100%)";

/* Step ids — copy (title, time, body, 3 tips) lives in the gettingStarted catalog
 * under `step.<id>.*`; tips are `step.<id>.tip1..tip3`. */
const STEP_IDS = ["signup", "configure", "project", "team", "invite"] as const;

type NeedAnimKind = "mail" | "team" | "project" | "phone";
const NEEDS: { anim: NeedAnimKind; id: string }[] = [
  { anim: "mail", id: "email" },
  { anim: "team", id: "employeeEmails" },
  { anim: "project", id: "project" },
  { anim: "phone", id: "smartphones" },
];

/* Minimalist animated icons (our MiniAnim style — pure ds-* keyframes, one accent
   colour, a subtle loop that mirrors what each item is for). */
function NeedAnim({ kind }: { kind: NeedAnimKind }) {
  return (
    <div className="relative grid h-12 w-12 place-items-center overflow-hidden rounded-2xl bg-white text-ds-accent ring-1 ring-line">
      {/* mail → an envelope with a notification ping (account activity) */}
      {kind === "mail" && (
        <>
          <Mail size={20} strokeWidth={2} />
          <span className="absolute right-2.5 top-2.5 flex h-2 w-2">
            <span className="ds-locate absolute inline-flex h-full w-full rounded-full bg-ds-accent" />
            <span className="relative h-2 w-2 rounded-full bg-ds-accent" />
          </span>
        </>
      )}

      {/* team → overlapping avatars popping in turn (one login per worker) */}
      {kind === "team" && (
        <div className="flex -space-x-1.5">
          {[0, 1, 2].map((i) => (
            <span key={i} className="ds-pop h-3.5 w-3.5 rounded-full bg-ds-accent ring-2 ring-white" style={{ animationDelay: `${i * 0.3}s` }} />
          ))}
        </div>
      )}

      {/* project → a folder with a "+" that pops (creating the first project) */}
      {kind === "project" && (
        <>
          <Folder size={20} strokeWidth={2} />
          <span className="ds-pop absolute bottom-1.5 right-1.5 grid h-4 w-4 place-items-center rounded-full bg-ds-accent text-white ring-2 ring-white">
            <Plus size={9} strokeWidth={3.5} />
          </span>
        </>
      )}

      {/* phone → a tap ripple over the screen (the one-tap check-in) */}
      {kind === "phone" && (
        <>
          <Smartphone size={20} strokeWidth={2} />
          <span className="absolute left-1/2 top-1/2 grid h-3 w-3 -translate-x-1/2 -translate-y-1/2 place-items-center">
            <span className="ds-locate absolute inline-flex h-full w-full rounded-full bg-ds-accent" />
            <span className="relative h-1.5 w-1.5 rounded-full bg-ds-accent" />
          </span>
        </>
      )}
    </div>
  );
}

const FIRST_WEEK_IDS = ["day1", "day3", "day7"] as const;

const NAV: { to: string; icon: LucideIcon; id: string }[] = [
  { to: "/trainings", icon: PlayCircle, id: "videos" },
  { to: "/faq", icon: HelpCircle, id: "faq" },
  { to: "/contact", icon: MessageCircle, id: "contact" },
];

const Y = "var(--ds-gold-ink)";
const B = "var(--ds-accent-deep)";
/* icon-grid tiles — `id` selects the label key `ctaGrid.<id>`; copy resolved in render. */
const CTA_ICONS: { id: string; Icon: LucideIcon; color: string }[] = [
  { id: "signUp", Icon: Rocket, color: B }, { id: "configure", Icon: Settings, color: Y },
  { id: "project", Icon: FolderPlus, color: B }, { id: "team", Icon: Users, color: Y },
  { id: "app", Icon: Smartphone, color: B }, { id: "check", Icon: Check, color: Y },
  { id: "hours", Icon: Clock, color: B }, { id: "reports", Icon: FileText, color: Y },
  { id: "reminders", Icon: Bell, color: B }, { id: "videos", Icon: PlayCircle, color: Y },
  { id: "help", Icon: HelpCircle, color: B }, { id: "chat", Icon: MessageCircle, color: Y },
  { id: "email", Icon: Mail, color: B },
];

export default function GettingStarted() {
  const { t } = useLocale();

  const setupSteps: WorkflowStep[] = STEP_IDS.map((id, i) => ({
    n: `0${i + 1}`,
    title: t(`gettingStarted:step.${id}.title`),
    body: t(`gettingStarted:step.${id}.body`),
    media: (
      <div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-ds-tint px-2.5 py-1 text-[12px] font-semibold text-ds-accent">
          <Clock size={12} strokeWidth={2.4} /> {t(`gettingStarted:step.${id}.time`)}
        </span>
        <ul className="mt-4 flex flex-col gap-2">
          {[1, 2, 3].map((n) => (
            <li key={n} className="flex items-start gap-2 text-ds-14 leading-relaxed text-ds-ink-soft">
              <Check size={14} strokeWidth={2.4} className="mt-0.5 shrink-0 text-ds-accent" />
              {t(`gettingStarted:step.${id}.tip${n}`)}
            </li>
          ))}
        </ul>
      </div>
    ),
  }));

  const ctaIcons: CtaGridIcon[] = CTA_ICONS.map(({ id, Icon, color }) => ({
    name: t(`gettingStarted:ctaGrid.${id}`),
    Icon,
    color,
  }));

  return (
    <>
      {/* 1 ── Blue hero */}
      <PageHeading
        eyebrow={t("gettingStarted:hero.eyebrow")}
        title={t("gettingStarted:hero.title")}
        intro={t("gettingStarted:hero.intro")}
        actions={<>
          <DSButton to={TRIAL} variant="solid-light">{t("gettingStarted:hero.startTrial")}</DSButton>
          <DSButton to="/trainings" variant="ghost">{t("gettingStarted:hero.watchOverview")}</DSButton>
        </>}
      />

      {/* 2 ── Before you start — bento with animated icons */}
      <MotionProvider>
        <section className="bg-bg font-ds">
          <Container className="py-16 md:py-20">
            <SectionHeading
              eyebrow={t("gettingStarted:needs.eyebrow")}
              title={t("gettingStarted:needs.title")}
              intro={t("gettingStarted:needs.intro")}
            />
            <div className="mt-10 rounded-3xl border border-line bg-surface p-2">
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {NEEDS.map((it, i) => (
                  <Reveal key={it.id} delay={i * 0.06} className="flex">
                    <div className="flex h-full w-full flex-col rounded-2xl bg-bg-subtle p-5">
                      <NeedAnim kind={it.anim} />
                      <h3 className="mt-4 font-display text-ds-18 font-semibold text-ds-ink">{t(`gettingStarted:need.${it.id}.label`)}</h3>
                      <p className="mt-1.5 flex-1 text-ds-14 leading-relaxed text-ds-ink-soft">{t(`gettingStarted:need.${it.id}.note`)}</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-semibold text-ds-pos">
                        <Check size={13} strokeWidth={3} /> {t("gettingStarted:needs.ready")}
                      </span>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </Container>
        </section>
      </MotionProvider>

      {/* 3 ── Full-screen video — poster, scrim, semi-transparent play button */}
      <section className="bg-bg font-ds">
        <div className="px-4 py-3">
          <LocaleLink to="/trainings" className="group relative block h-[95vh] overflow-hidden rounded-[32px] bg-ds-hero">
            <img src={cmsImage(t, "gettingStarted:video.image", VIDEO_POSTER)} alt={t("gettingStarted:video.posterAlt")} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
            <div aria-hidden className="absolute inset-0" style={{ background: SCRIM }} />

            {/* Play button — semi-transparent glass with a pulsing ring */}
            <span className="absolute left-1/2 top-1/2 grid h-24 w-24 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white/15 ring-1 ring-white/50 backdrop-blur-md transition-transform duration-300 group-hover:scale-110">
              <span aria-hidden className="absolute inset-0 animate-ping rounded-full ring-1 ring-white/40" />
              <Play size={30} className="relative ml-1 text-white" fill="currentColor" />
            </span>

            {/* Caption */}
            <div className="absolute inset-x-0 bottom-0 p-8 md:p-12">
              <SectionHeading tone="dark" eyebrow={t("gettingStarted:video.eyebrow")} title={t("gettingStarted:video.title")} />
            </div>
          </LocaleLink>
        </div>
      </section>

      {/* 4 ── The setup — stacking cards on lighting-up blue */}
      <Workflow
        eyebrow={<Eyebrow dark>{t("gettingStarted:setup.eyebrow")}</Eyebrow>}
        title={<>{t("gettingStarted:setup.titleLead")} <span className="text-ds-ink">{t("gettingStarted:setup.titleAccent")}</span></>}
        description={t("gettingStarted:setup.description")}
        steps={setupSteps}
        cta={{
          title: t("gettingStarted:setup.cta.title"),
          body: t("gettingStarted:setup.cta.body"),
          action: <DSButton to={TRIAL} variant="solid-light">{t("gettingStarted:setup.cta.startTrial")}</DSButton>,
        }}
      />

      {/* 5 ── First week — three sequential lighting-up cards */}
      <ConnectedSteps
        eyebrow={<Eyebrow>{t("gettingStarted:firstWeek.eyebrow")}</Eyebrow>}
        title={<>{t("gettingStarted:firstWeek.titleLead")} <span className="text-ds-accent">{t("gettingStarted:firstWeek.titleAccent")}</span></>}
        description={t("gettingStarted:firstWeek.description")}
        steps={FIRST_WEEK_IDS.map((id) => ({ title: t(`gettingStarted:firstWeek.${id}.title`), body: t(`gettingStarted:firstWeek.${id}.body`) }))}
      />

      {/* 7 ── Help & resources — navigation cards in our style */}
      <MotionProvider>
        <section className="bg-bg-subtle font-ds">
          <Container className="py-16 md:py-20">
            <SectionHeading eyebrow={t("gettingStarted:help.eyebrow")} title={t("gettingStarted:help.title")} />
            <div className="mt-8 rounded-3xl border border-line bg-surface p-2">
              <div className="grid gap-2 sm:grid-cols-3">
                {NAV.map((card) => {
                  const Icon = card.icon;
                  return (
                    <Reveal key={card.id} className="flex">
                      <LocaleLink to={card.to} className="group flex h-full w-full flex-col rounded-2xl bg-bg-subtle p-5 md:p-6 transition-colors hover:bg-white">
                        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-ds-tint text-ds-accent transition-transform duration-300 ease-out group-hover:-rotate-6 group-hover:scale-110">
                          <Icon size={20} strokeWidth={2} />
                        </span>
                        <h3 className="mt-4 font-display text-ds-18 font-semibold text-ink">{t(`gettingStarted:nav.${card.id}.title`)}</h3>
                        <p className="mt-2 flex-1 text-ds-14 leading-relaxed text-ink-soft">{t(`gettingStarted:nav.${card.id}.body`)}</p>
                        <span className="mt-4 inline-flex items-center gap-1 text-[13px] font-semibold text-primary opacity-0 transition-opacity group-hover:opacity-100">
                          {t(`gettingStarted:nav.${card.id}.cta`)} <ArrowRight size={14} strokeWidth={2} />
                        </span>
                      </LocaleLink>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </Container>
        </section>
      </MotionProvider>

      {/* 8 ── Closing CTA — icon-tile grid */}
      <section className="bg-bg font-ds">
        <IconGridCta
          items={ctaIcons}
          eyebrow={<Eyebrow>{t("gettingStarted:closing.eyebrow")}</Eyebrow>}
          title={t("gettingStarted:closing.title")}
          body={t("gettingStarted:closing.body")}
          actions={
            <div className="flex flex-wrap items-center justify-center gap-3">
              <DSButton to={TRIAL} variant="solid">{t("gettingStarted:closing.startTrial")}</DSButton>
              <DSButton to="/faq" variant="outline">{t("gettingStarted:closing.readFaq")}</DSButton>
            </div>
          }
        />
      </section>
    </>
  );
}

